import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Avatar } from "../Avatar/Avatar";
import { visibleScrollbar } from "../../utils/functions/get-visible-scrollbar";
import CheckIcon from "../../assets/svg/check-icon";
import { useGetAllAgentsQuery, useGetUsersQuery } from "../../redux/services/userManagementApi";
import ChevronDownIcon from "../../assets/svg/chevron-down-icon";
import SearchIcon from "../../assets/svg/search-icon";
import { rolesMapping } from "../../utils/constants/rolesMapping";
import Button from "../Button/Button";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

interface UserItem {
  id: string;
  name: string;
  role: string;
  status: string;
  profile_picture: string | null;
  email: string;
}

interface AgentDropdownProps {
  selectedAgentIds: string[];
  onSelect: (ids: string[]) => void;
  maxAgents?: number;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  useUsersApi?: boolean;
}

export const AgentDropdown: React.FC<AgentDropdownProps> = ({
  selectedAgentIds = [],
  onSelect,
  maxAgents = 4,
  label = "ASSIGN AGENTS",
  placeholder = "Select agent(s)",
  disabled = false,
  className = "",
  useUsersApi = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [tempSelected, setTempSelected] = useState<Set<string>>(
    new Set(selectedAgentIds)
  );
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const userId = useSelector((state: RootState) => state.auth.user?._id);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { data: allAgentsData, isFetching: isFetchingAgents } = useGetAllAgentsQuery(undefined, { skip: useUsersApi });
  const { data: allUsersData, isFetching: isFetchingUsers } = useGetUsersQuery(
    { current_page: 1, limit: 200 },
    { skip: !useUsersApi }
  );
  const isFetching = useUsersApi ? isFetchingUsers : isFetchingAgents;

  const users: UserItem[] = useUsersApi
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ? ((allUsersData as any)?.user_list?.map((item: any) => ({
        id: item?.user?.id || item?._id,
        name: `${item?.user?.name || item?.name}${(item?.user?.id || item?._id) === userId ? " (Me)" : ""}`,
        role: item?.role?.value || item?.role || "",
        status: item?.status?.value || item?.status || "",
        profile_picture: null,
        email: item?.user?.email || "",
      })) || [])
    : (allAgentsData?.items?.map(
        (u: {
          _id: string;
          name: string;
          role: string;
          status: string;
          email: string;
        }) => ({
          id: u?._id,
          name: `${u?.name}${u?._id === userId ? " (Me)" : ""}`,
          role: u?.role,
          status: u?.status,
          profile_picture: null,
          email: u?.email,
        })
      ) || []);

  const filteredUsers = searchQuery.trim()
    ? users.filter((u) =>
        (u?.name || "").toLowerCase().includes(searchQuery.toLowerCase())
      )
    : users;

  const selectedUsers = users.filter((u) => tempSelected.has(u?.id || ""));
  const unselectedUsers = filteredUsers.filter(
    (u) => !tempSelected.has(u?.id || "")
  );

  useEffect(() => {
    setTempSelected(new Set(selectedAgentIds));
  }, [selectedAgentIds]);

  const calculatePosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;

    const viewportHeight = window.innerHeight;
    const dropdownHeight = dropdownRef.current?.offsetHeight || 292;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    const top =
      spaceBelow < dropdownHeight && spaceAbove > spaceBelow
        ? rect.top + scrollTop - dropdownHeight
        : rect.bottom + scrollTop + 5;

    const left = rect.left + scrollLeft;
    const width = rect.width;

    setDropdownPosition({ top, left, width });
  };

  const handleCancel = useCallback(() => {
    setTempSelected(new Set(selectedAgentIds));
    setIsOpen(false);
    setSearchQuery("");
  }, [selectedAgentIds]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
        handleCancel();
      }
    };

    if (isOpen) {
      calculatePosition();
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("resize", calculatePosition);
      window.addEventListener("scroll", calculatePosition, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", calculatePosition);
      window.removeEventListener("scroll", calculatePosition, true);
    };
  }, [isOpen, handleCancel]);

  const handleToggleUser = (userId: string) => {
    const newTempSelected = new Set(tempSelected);
    if (newTempSelected.has(userId)) {
      newTempSelected.delete(userId);
    } else {
      if (newTempSelected.size < maxAgents) {
        newTempSelected.add(userId);
      }
    }
    setTempSelected(newTempSelected);
  };

  const handleApply = () => {
    onSelect(Array.from(tempSelected));
    setIsOpen(false);
    setSearchQuery("");
  };

  const handleShowMore = () => {
    setShowMore(isOpen ? false : true); // if the dropdown is open then dont trigger tooltip popup
  };

  const handleHideMore = () => {
    setShowMore(false);
  };

  const roleDot = (role?: string) => {
    const roleLower = (role || "").toLowerCase();
    const color = roleLower.includes("super")
      ? "#22C55E"
      : roleLower.includes("admin")
        ? "#FACC15"
        : "#9CA3AF";
    return (
      <span
        className="inline-block w-2 h-2 rounded-full"
        style={{ backgroundColor: color }}
      />
    );
  };

  return (
    <div className="w-full">
      {/* {label && (
        <label className="block text-sm font-medium text-heading-text dark:text-heading-text-dk mb-2">
          {label}
        </label>
      )} */}

      <div className={`relative ${className}`}>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`w-full min-h-[40px] px-1.5 py-2 pr-4 text-left bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-lg flex items-center justify-between gap-1.5 transition-colors ${
            disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
          }`}
        >
          <div className="flex-1 flex items-center gap-2 ">
            {selectedUsers.length === 0 ? (
              <span className="text-gray-placeholder dark:text-gray-placeholder-dk text-sm">
                {placeholder}
              </span>
            ) : (
              <div className="flex items-center flex-wrap">
                {selectedUsers && selectedUsers.length > 0 && (
                  <div
                    key={selectedUsers[0].id}
                    className="flex items-center gap-1.5 py-1 rounded-md"
                  >
                    <Avatar
                      text={selectedUsers[0].name || ""}
                      maxChars={1}
                      size={16}
                    />
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2">
                        <span className="max-w-[90px] truncate text-sm text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
                          {selectedUsers[0].name}
                        </span>
                        <span className="text-sm text-border-gray dark:text-border-gray-dk">
                          {" "}
                          |{" "}
                        </span>
                        <span className="text-sm text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
                          {roleDot(selectedUsers[0].role)}
                        </span>
                        <span className="text-sm text-verba-indigo dark:text-verba-indigo-dk whitespace-nowrap">
                          {
                            rolesMapping[
                              selectedUsers[0].role as keyof typeof rolesMapping
                            ]
                          }
                        </span>
                      </div>
                      {selectedUsers.length > 1 && (
                        <div
                          className="relative flex items-center"
                          onMouseEnter={handleShowMore}
                          onMouseLeave={handleHideMore}
                        >
                          <button
                            className="text-sm text-verba-indigo dark:text-verba-indigo-dk border border-border-gray dark:border-border-gray-dk rounded-sm px-1 py-0.5 whitespace-nowrap"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            +{selectedUsers.length - 1} more
                          </button>
                          {selectedUsers &&
                            selectedUsers.length > 1 &&
                            showMore && (
                              <div
                                className="absolute bottom-full right-full translate-x-1/3 z-9999999 bg-popper-bg/80 dark:bg-popper-bg-dk/80 backdrop-blur-[20px] rounded-lg p-4 shadow-popper-blur-effect flex flex-col gap-3 min-w-max"
                                // onMouseEnter={handleShowMore}
                                // onMouseLeave={handleHideMore}
                              >
                                {selectedUsers.slice(1).map((user) => (
                                  <div
                                    className="flex items-center gap-2 relative z-10"
                                    key={user.id}
                                  >
                                    <Avatar
                                      text={user.name || ""}
                                      maxChars={1}
                                      size={16}
                                    />
                                    <span className="max-w-[120px] truncate text-sm text-verba-indigo dark:text-verba-indigo-dk">
                                      {user.name}
                                    </span>
                                    <span className="text-sm text-border-gray dark:text-border-gray-dk">
                                      {" "}
                                      |{" "}
                                    </span>
                                    <span className="text-sm text-verba-indigo dark:text-verba-indigo-dk">
                                      {roleDot(user.role)}
                                    </span>
                                    <span className="text-sm text-verba-indigo dark:text-verba-indigo-dk">
                                      {
                                        rolesMapping[
                                          user.role as keyof typeof rolesMapping
                                        ]
                                      }
                                    </span>
                                  </div>
                                ))}

                                <div className="absolute -bottom-2 right-10 -translate-x-1/3 w-4 h-4 rotate-45 bg-popper-bg dark:bg-popper-bg-dk backdrop-blur-[20px]" />
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <ChevronDownIcon
            className={`shrink-0 w-3 h-3 text-verba-indigo dark:text-verba-indigo-dk transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {isOpen &&
          dropdownPosition &&
          createPortal(
            <div
              ref={dropdownRef}
              className={`fixed z-[1000000] max-h-[292px] overflow-hidden backdrop-blur-[10px]
            bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk 
            rounded-[8px] shadow-popper-blur-effect flex flex-col`}
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
              }}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <div className="p-3">
                <div className="text-xs font-semibold text-data dark:text-data-dk uppercase">
                  {label}
                </div>
                <div className="text-xs text-gray-placeholder dark:text-gray-placeholder-dk">
                  {`Assign this lead to up to ${maxAgents} agents.`}
                </div>
              </div>

              <div className="px-2.5 pb-1">
                <div className="relative">
                  <SearchIcon className="absolute left-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-verba-indigo dark:text-verba-indigo-dk" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search"
                    className="w-full py-2 text-sm pl-5 text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk focus:outline-none outline-none focus:border-border-gray focus:dark:border-border-gray-dk"
                  />
                </div>
              </div>

              <div
                className={`flex-1 overflow-y-auto ${visibleScrollbar(
                  "[&::-webkit-scrollbar]:w-[4px]"
                )}`}
              >
                {isFetching ? (
                  <div className="px-3 py-3 text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                    Loading agents...
                  </div>
                ) : (
                  <>
                    {selectedUsers.length > 0 && (
                      <div className="divide-y divide-border-gray dark:divide-border-gray-dk">
                        {selectedUsers.map((user) => (
                          <Button
                            key={user.id}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleToggleUser(user.id);
                            }}
                            secondaryButtonContainerClass="w-full !p-0"
                            innerText={
                              <div className={`w-full flex items-center gap-2`}>
                                <Avatar
                                  text={user.name || ""}
                                  maxChars={1}
                                  size={18}
                                />
                                <div className="flex-1 flex items-center justify-between min-w-0">
                                  <div className="max-w-[100px] text-sm text-verba-indigo dark:text-verba-indigo-dk truncate">
                                    {user.name}
                                  </div>
                                  {user.role && (
                                    <div className="flex items-center gap-1 text-xxs text-dark-gray dark:text-dark-gray-dk">
                                      {roleDot(user.role)}
                                      <span className="max-w-[65px] truncate capitalize">
                                        {user.role}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div
                                  className={`flex items-center justify-center w-[14px] h-[14px] border rounded-[3px] ${
                                    tempSelected.has(user.id)
                                      ? "bg-verba-blue text-fixed-white border-verba-blue dark:bg-verba-blue-dk dark:border-verba-blue-dk"
                                      : "border-gray-100 dark:border-gray-100-dk"
                                  }`}
                                >
                                  {tempSelected.has(user.id) && (
                                    <CheckIcon className="w-3 h-3 text-fixed-white dark:text-fixed-white-dk" />
                                  )}
                                </div>
                              </div>
                            }
                            innerTextClass="w-full"
                            buttonContainerClass={`w-full py-[10px] px-3 ${
                              tempSelected.has(user.id)
                                ? "bg-card-highlight-2 dark:bg-card-highlight-2-dk "
                                : " hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                            }`}
                          />
                        ))}
                      </div>
                    )}

                    <div className="px-[12px] py-[8px] text-xxs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-y border-border-gray dark:border-border-gray-dk">
                      ALL AGENTS
                    </div>

                    {unselectedUsers.length === 0 ? (
                      <div className="px-3 py-3 text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                        {searchQuery.trim()
                          ? "No agents found"
                          : "No agents available"}
                      </div>
                    ) : (
                      <div className="divide-y divide-border-gray dark:divide-border-gray-dk">
                        {unselectedUsers.map((user) => {
                          const isSelected = tempSelected.has(user.id);
                          const canAddMore =
                            tempSelected.size < maxAgents || isSelected;
                          return (
                            <Button
                              key={user.id}
                              disabled={!canAddMore}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleToggleUser(user.id);
                              }}
                              secondaryButtonContainerClass="w-full !p-0"
                              innerText={
                                <div
                                  className={`w-full flex items-center gap-2`}
                                >
                                  <Avatar
                                    text={user.name || ""}
                                    maxChars={1}
                                    size={18}
                                  />
                                  <div className="flex-1 flex items-center justify-between min-w-0">
                                    <div className="max-w-[100px] text-sm text-verba-indigo dark:text-verba-indigo-dk truncate">
                                      {user.name}
                                    </div>
                                    {user.role && (
                                      <div className="flex items-center gap-1 text-xxs text-dark-gray dark:text-dark-gray-dk">
                                        {roleDot(user.role)}
                                        <span className="max-w-[65px] truncate capitalize">
                                          {user.role}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  <div
                                    className={`flex items-center justify-center w-[14px] h-[14px] border rounded-[3px] ${
                                      isSelected
                                        ? "bg-verba-blue text-fixed-white border-verba-blue dark:bg-verba-blue-dk dark:border-verba-blue-dk"
                                        : "border-gray-100 dark:border-gray-100-dk"
                                    }`}
                                  >
                                    {isSelected && (
                                      <CheckIcon className="w-3 h-3 text-fixed-white dark:text-fixed-white-dk" />
                                    )}
                                  </div>
                                </div>
                              }
                              innerTextClass="w-full"
                              buttonContainerClass={`w-full py-[10px] px-3 ${
                                isSelected
                                  ? "bg-card-highlight-2 dark:bg-card-highlight-2-dk "
                                  : " hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                              } ${
                                !canAddMore
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                            />
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="flex items-center justify-between gap-2 px-[12px] py-[10px]">
                <Button
                  innerText="Cancel"
                  innerTextClass="text-heading-text dark:text-heading-text-dk text-md font-semibold"
                  buttonContainerClass="min-w-[104px] w-fit px-2 py-1.5 rounded-lg hover:bg-hover-effect dark:hover:bg-hover-effect-dk"
                  onClick={handleCancel}
                />

                <Button
                  onClick={handleApply}
                  disabled={
                    tempSelected.size === selectedAgentIds.length &&
                    Array.from(tempSelected).every((id) =>
                      selectedAgentIds.includes(id)
                    )
                  }
                  innerText={`Assign (${tempSelected.size})`}
                  innerTextClass={`text-md font-semibold ${
                    (tempSelected.size === selectedAgentIds.length &&
                      Array.from(tempSelected).every((id) =>
                        selectedAgentIds.includes(id)
                      )) ||
                    (selectedAgentIds.length > 0 && tempSelected.size === 0)
                      ? " text-pure-white dark:text-pure-white-dk "
                      : " text-pure-white dark:text-pure-white-dk group-hover:text-pure-white dark:group-hover:text-pure-white! "
                  }`}
                  buttonContainerClass={`min-w-[104px] group rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk border border-verba-indigo dark:border-verba-indigo-dk py-1.5 px-2 disabled:opacity-25 disabled:cursor-not-allowed! ${
                    (tempSelected.size === selectedAgentIds.length &&
                      Array.from(tempSelected).every((id) =>
                        selectedAgentIds.includes(id)
                      )) ||
                    (selectedAgentIds.length > 0 && tempSelected.size === 0)
                      ? ""
                      : " hover:bg-verba-blue dark:hover:bg-verba-blue-dk hover:border-verba-blue hover:dark:border-verba-blue-dk"
                  }`}
                />
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
};

export default AgentDropdown;
